import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getEmployees, createEmployee, getDepartments, getDesignations, register } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { UserPlus, User, Briefcase, CreditCard, Phone, FileText } from 'lucide-react';
import { toast } from 'sonner';

const EnhancedEmployeeManagement = ({ user, setUser }) => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  
  const [formData, setFormData] = useState({
    // User
    full_name: '',
    email: '',
    password: '',
    role: 'employee',
    
    // Basic
    employee_id: '',
    department_id: '',
    designation_id: '',
    manager_id: '',
    
    // Personal
    phone: '',
    alternate_phone: '',
    personal_email: '',
    address: '',
    permanent_address: '',
    date_of_birth: '',
    gender: '',
    marital_status: '',
    blood_group: '',
    
    // Job
    date_of_joining: '',
    salary: '',
    employee_type: 'Permanent',
    probation_period: '6',
    notice_period: '60',
    work_location: '',
    tds_override: '',
    
    // KYC
    aadhaar_number: '',
    pan_number: '',
    uan_number: '',
    esic_number: '',
    
    // Bank
    bank_name: '',
    bank_account_number: '',
    ifsc_code: '',
    bank_branch: '',
    
    // Emergency
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [empRes, deptRes, desigRes] = await Promise.all([
        getEmployees(),
        getDepartments(),
        getDesignations()
      ]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
      setDesignations(desigRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Create user
      const userRes = await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        role: formData.role
      });

      // Create employee with all fields
      await createEmployee({
        user_id: userRes.data.id,
        employee_id: formData.employee_id,
        department_id: formData.department_id,
        designation_id: formData.designation_id,
        manager_id: formData.manager_id || null,
        
        phone: formData.phone,
        alternate_phone: formData.alternate_phone || null,
        personal_email: formData.personal_email || null,
        address: formData.address || null,
        permanent_address: formData.permanent_address || null,
        date_of_birth: formData.date_of_birth || null,
        gender: formData.gender || null,
        marital_status: formData.marital_status || null,
        blood_group: formData.blood_group || null,
        
        date_of_joining: formData.date_of_joining,
        salary: parseFloat(formData.salary),
        employee_type: formData.employee_type,
        probation_period: parseInt(formData.probation_period),
        notice_period: parseInt(formData.notice_period),
        work_location: formData.work_location || null,
        tds_override: formData.tds_override === '' ? null : parseFloat(formData.tds_override),
        
        aadhaar_number: formData.aadhaar_number || null,
        pan_number: formData.pan_number || null,
        uan_number: formData.uan_number || null,
        esic_number: formData.esic_number || null,
        
        bank_name: formData.bank_name || null,
        bank_account_number: formData.bank_account_number || null,
        ifsc_code: formData.ifsc_code || null,
        bank_branch: formData.bank_branch || null,
        
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        emergency_contact_relation: formData.emergency_contact_relation || null
      });

      toast.success('Employee created successfully');
      setDialogOpen(false);
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create employee');
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '', email: '', password: '', role: 'employee',
      employee_id: '', department_id: '', designation_id: '', manager_id: '',
      phone: '', alternate_phone: '', personal_email: '', address: '', permanent_address: '',
      date_of_birth: '', gender: '', marital_status: '', blood_group: '',
      date_of_joining: '', salary: '', employee_type: 'Permanent', probation_period: '6',
      notice_period: '60', work_location: '', tds_override: '',
      aadhaar_number: '', pan_number: '', uan_number: '', esic_number: '',
      bank_name: '', bank_account_number: '', ifsc_code: '', bank_branch: '',
      emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relation: ''
    });
    setActiveTab('personal');
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6" data-testid="enhanced-employee-management">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Enhanced Employee Management</h1>
            <p className="text-slate-600 mt-1">Complete employee data management</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full" data-testid="add-employee-btn">
                <UserPlus className="mr-2" size={20} />
                Add Employee
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
              <DialogHeader>
                <DialogTitle>Add New Employee - Complete Details</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="personal">
                      <User size={16} className="mr-2" />Personal
                    </TabsTrigger>
                    <TabsTrigger value="job">
                      <Briefcase size={16} className="mr-2" />Job
                    </TabsTrigger>
                    <TabsTrigger value="kyc">
                      <FileText size={16} className="mr-2" />KYC
                    </TabsTrigger>
                    <TabsTrigger value="bank">
                      <CreditCard size={16} className="mr-2" />Bank
                    </TabsTrigger>
                    <TabsTrigger value="emergency">
                      <Phone size={16} className="mr-2" />Emergency
                    </TabsTrigger>
                  </TabsList>

                  {/* Personal Details Tab */}
                  <TabsContent value="personal" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Full Name *</Label>
                        <Input
                          value={formData.full_name}
                          onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Official Email *</Label>
                        <Input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Password *</Label>
                        <Input
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Personal Email</Label>
                        <Input
                          type="email"
                          value={formData.personal_email}
                          onChange={(e) => setFormData({...formData, personal_email: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone *</Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Alternate Phone</Label>
                        <Input
                          value={formData.alternate_phone}
                          onChange={(e) => setFormData({...formData, alternate_phone: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Date of Birth</Label>
                        <Input
                          type="date"
                          value={formData.date_of_birth}
                          onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Gender</Label>
                        <Select value={formData.gender} onValueChange={(v) => setFormData({...formData, gender: v})}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Male">Male</SelectItem>
                            <SelectItem value="Female">Female</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Marital Status</Label>
                        <Select value={formData.marital_status} onValueChange={(v) => setFormData({...formData, marital_status: v})}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Single">Single</SelectItem>
                            <SelectItem value="Married">Married</SelectItem>
                            <SelectItem value="Divorced">Divorced</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Blood Group</Label>
                        <Select value={formData.blood_group} onValueChange={(v) => setFormData({...formData, blood_group: v})}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(bg => (
                              <SelectItem key={bg} value={bg}>{bg}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label>Current Address</Label>
                        <Input
                          value={formData.address}
                          onChange={(e) => setFormData({...formData, address: e.target.value})}
                        />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label>Permanent Address</Label>
                        <Input
                          value={formData.permanent_address}
                          onChange={(e) => setFormData({...formData, permanent_address: e.target.value})}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Job Details Tab */}
                  <TabsContent value="job" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Employee ID *</Label>
                        <Input
                          value={formData.employee_id}
                          onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Department *</Label>
                        <Select value={formData.department_id} onValueChange={(v) => setFormData({...formData, department_id: v})} required>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {departments.map(d => (
                              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Designation *</Label>
                        <Select value={formData.designation_id} onValueChange={(v) => setFormData({...formData, designation_id: v})} required>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {designations.map(d => (
                              <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Date of Joining *</Label>
                        <Input
                          type="date"
                          value={formData.date_of_joining}
                          onChange={(e) => setFormData({...formData, date_of_joining: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Monthly Salary (₹) *</Label>
                        <Input
                          type="number"
                          value={formData.salary}
                          onChange={(e) => setFormData({...formData, salary: e.target.value})}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Monthly TDS Override (₹)</Label>
                        <Input
                          type="number"
                          placeholder="Auto from tax regime if empty"
                          value={formData.tds_override}
                          onChange={(e) => setFormData({...formData, tds_override: e.target.value})}
                          data-testid="tds-override-input"
                        />
                        <p className="text-[11px] text-slate-500">Set a fixed monthly TDS to override the slab-based auto-calculation on payslips (0 forces no TDS).</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Employee Type</Label>
                        <Select value={formData.employee_type} onValueChange={(v) => setFormData({...formData, employee_type: v})}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Permanent">Permanent</SelectItem>
                            <SelectItem value="Contract">Contract</SelectItem>
                            <SelectItem value="Intern">Intern</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Probation Period (months)</Label>
                        <Input
                          type="number"
                          value={formData.probation_period}
                          onChange={(e) => setFormData({...formData, probation_period: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Notice Period (days)</Label>
                        <Input
                          type="number"
                          value={formData.notice_period}
                          onChange={(e) => setFormData({...formData, notice_period: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Work Location</Label>
                        <Input
                          value={formData.work_location}
                          onChange={(e) => setFormData({...formData, work_location: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Reporting Manager</Label>
                        <Select value={formData.manager_id} onValueChange={(v) => setFormData({...formData, manager_id: v})}>
                          <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
                          <SelectContent>
                            {employees.filter(e => ['admin', 'hr_manager', 'manager'].includes(e.role)).map(e => (
                              <SelectItem key={e.id} value={e.id}>{e.employee_id}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </TabsContent>

                  {/* KYC Tab */}
                  <TabsContent value="kyc" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Aadhaar Number</Label>
                        <Input
                          value={formData.aadhaar_number}
                          onChange={(e) => setFormData({...formData, aadhaar_number: e.target.value})}
                          maxLength={12}
                          placeholder="XXXX-XXXX-XXXX"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>PAN Number</Label>
                        <Input
                          value={formData.pan_number}
                          onChange={(e) => setFormData({...formData, pan_number: e.target.value})}
                          maxLength={10}
                          placeholder="ABCDE1234F"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>UAN Number (PF)</Label>
                        <Input
                          value={formData.uan_number}
                          onChange={(e) => setFormData({...formData, uan_number: e.target.value})}
                          placeholder="101234567890"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>ESIC Number</Label>
                        <Input
                          value={formData.esic_number}
                          onChange={(e) => setFormData({...formData, esic_number: e.target.value})}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Bank Tab */}
                  <TabsContent value="bank" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Bank Name</Label>
                        <Input
                          value={formData.bank_name}
                          onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Account Number</Label>
                        <Input
                          value={formData.bank_account_number}
                          onChange={(e) => setFormData({...formData, bank_account_number: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>IFSC Code</Label>
                        <Input
                          value={formData.ifsc_code}
                          onChange={(e) => setFormData({...formData, ifsc_code: e.target.value})}
                          maxLength={11}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Branch</Label>
                        <Input
                          value={formData.bank_branch}
                          onChange={(e) => setFormData({...formData, bank_branch: e.target.value})}
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Emergency Contact Tab */}
                  <TabsContent value="emergency" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Contact Name</Label>
                        <Input
                          value={formData.emergency_contact_name}
                          onChange={(e) => setFormData({...formData, emergency_contact_name: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Contact Phone</Label>
                        <Input
                          value={formData.emergency_contact_phone}
                          onChange={(e) => setFormData({...formData, emergency_contact_phone: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Relation</Label>
                        <Input
                          value={formData.emergency_contact_relation}
                          onChange={(e) => setFormData({...formData, emergency_contact_relation: e.target.value})}
                          placeholder="e.g., Spouse, Parent, Sibling"
                        />
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>

                <div className="flex gap-3">
                  <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700 rounded-full">
                    Create Employee
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Employee List */}
        <div className="grid grid-cols-1 gap-4">
          {employees.length === 0 ? (
            <Card className="border border-slate-100 shadow-sm bg-white">
              <CardContent className="p-12 text-center bg-white">
                <p className="text-slate-500">No employees found. Add your first employee!</p>
              </CardContent>
            </Card>
          ) : (
            employees.map((emp, index) => (
              <Card key={emp.id} className="border border-slate-100 shadow-sm hover:shadow-md transition-all bg-white">
                <CardContent className="p-6 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center font-bold">
                        {emp.employee_id?.substring(0, 2) || 'EM'}
                      </div>
                      <div className="space-y-2">
                        <div>
                          <h3 className="font-semibold text-slate-900">{emp.employee_id}</h3>
                          <p className="text-sm text-slate-600">
                            {emp.employee_type} • Joined {emp.date_of_joining}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className="text-slate-600">📞 {emp.phone}</span>
                          {emp.pan_number && <span className="text-slate-600">🆔 PAN: {emp.pan_number}</span>}
                          {emp.bank_name && <span className="text-slate-600">🏦 {emp.bank_name}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-indigo-600 mono">₹{emp.salary?.toLocaleString('en-IN')}</p>
                      <p className="text-xs text-slate-500">per month</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
};

export default EnhancedEmployeeManagement;
